/** @jsxRuntime classic */
/** @jsx jsx */
import { Fragment, memo, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import Chip from "./";
import { Pdf, Word, Sheet, Presentation, Image, Compressed, Another } from "../../icons";
import { capitalize, getFileType } from "../../utils";
import { ButtonIcon, ModalSample, Slider, SliderItem } from "../";
const FileTypeIcon = {
  Pdf,
  Word,
  Sheet,
  Presentation,
  Image,
  Compressed,
  Another
};
const AttachChip = /*#__PURE__*/memo(({
  file,
  maxLength,
  onView,
  onDownload,
  onRemove,
  ...props
}) => {
  const moreActionRef = useRef(null);
  const [open, setOpen] = useState(false);
  const MoreAction = css`
        display: none;
        justify-content: space-evenly;
        align-items: center;
        position: absolute;
        top: -34px;
        right: -1px;
        width: 120px;
        height: 36px;
        background: #ffffff;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        &.DGN-UI-Chip-show {
            display: flex;
        }
    `;
  const getStartIcon = () => {
    var _file$FileName;
    const lastIndexOfDot = (_file$FileName = file.FileName) === null || _file$FileName === void 0 ? void 0 : _file$FileName.lastIndexOf('.');
    if (!lastIndexOfDot) return null;
    const fileExt = file.FileName.slice(lastIndexOfDot + 1);
    if (!fileExt) return null;
    const fileType = getFileType(fileExt);
    const FileIcon = FileTypeIcon[capitalize(fileType)];
    return jsx(FileIcon, {
      size: 'small',
      viewBox: true
    });
  };
  const clickOutAction = event => {
    if (!moreActionRef.current) {
      document.removeEventListener('click', clickOutAction);
      return;
    }
    if (!moreActionRef.current.contains(event.target)) {
      moreActionRef.current.classList.remove('DGN-UI-Chip-show');
      document.removeEventListener('click', clickOutAction);
    }
  };
  const onShowMoreOption = () => {
    if (!moreActionRef.current.classList.contains('DGN-UI-Chip-show')) {
      moreActionRef.current.classList.add('DGN-UI-Chip-show');
      setTimeout(() => {
        document.addEventListener('click', clickOutAction);
      }, 100);
    }
  };
  const viewAction = (e, file) => {
    if (onView) {
      onView(e, file);
    } else {
      setOpen(true);
    }
  };
  const downloadAction = (e, file = {}) => {
    if (onDownload) onDownload(e, file);else if (file.URL) {
      fetch(file.URL).then(resp => resp.blob()).then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        // the filename you want
        a.download = file.FileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }).catch(() => alert('File not found'));
    }
  };
  const deleteAction = (e, file) => {
    if (onRemove) onRemove(e, file);
    setTimeout(() => {
      if (moreActionRef.current && moreActionRef.current.parentNode) moreActionRef.current.parentNode.remove();
    }, 0);
  };
  const renderLabel = label => {
    if (maxLength && label && label.length > maxLength) {
      label = label.slice(0, maxLength) + '...';
    }
    return label;
  };
  const attachView = useMemo(() => {
    return jsx(ModalSample, {
      open: open,
      title: file.FileName,
      onClose: () => setOpen(false)
    }, jsx(Slider, null, jsx(SliderItem, {
      url: file.URL
    })));
  }, [open]);
  return jsx(Fragment, null, jsx(Chip, {
    ...props,
    clearAble: false,
    label: renderLabel(file.FileName),
    startIcon: getStartIcon(),
    endIcon: jsx(ButtonIcon, {
      viewType: 'ghost',
      name: 'more',
      size: 'tiny',
      onClick: onShowMoreOption
    })
  }, jsx("div", {
    css: MoreAction,
    ref: moreActionRef
  }, jsx(ButtonIcon, {
    viewType: 'ghost',
    name: 'view',
    onClick: e => viewAction(e, file)
  }), jsx(ButtonIcon, {
    viewType: 'ghost',
    name: 'download',
    onClick: e => downloadAction(e, file)
  }), jsx(ButtonIcon, {
    viewType: 'ghost',
    name: 'delete',
    onClick: e => deleteAction(e, file)
  }))), attachView);
});
AttachChip.propTypes = {
  /** the file info, such as:<br/>
   * {<br/>
   *  URL: https://url.com/pathname,<br/>
   *  FileName: file.name,<br/>
   * }
   */
  file: PropTypes.shape({
    URL: PropTypes.string.isRequired,
    FileName: PropTypes.string.isRequired
  }).isRequired,
  /** max length content to display was allowed */
  maxLength: PropTypes.number,
  /** any props else of Chip component */
  props: PropTypes.any
};
export default AttachChip;